import { computed } from 'vue';
import { ModalErrorStates } from '@/models/modal/modal.enum';
import {
	HttpErrorResponse, HttpErrorResponseWithErrorCode, InvalidCampaignPropertyCategories,
	InvalidCampaignPropertyDetails, InvalidCampaignPropertyKeywords
} from '@/models/error.interface';
import redshiftTracker from '@/infrastructure/redshift-tracker';
import rollbarOperator from '@/infrastructure/rollbar-operator';
import store from '@/store';

class CampaignErrorPresenter {
	private nameOfBackEndClass = 'CampaignEditorGetResultValidator:';
	private defaultValueSeparator = 'with value:';

	public invalidCampaignPropertyMessage = computed((): string => {
		const category = this.invalidCampaignPropertyDetails.value.category;
		const value = this.invalidCampaignPropertyDetails.value.value;
		switch (category) {
			case InvalidCampaignPropertyCategories.PARTNERSHIP_TYPE:
			case InvalidCampaignPropertyCategories.REGION:
			case InvalidCampaignPropertyCategories.BUDGET:
			case InvalidCampaignPropertyCategories.ADWORDS_START:
			case InvalidCampaignPropertyCategories.ADWORDS_END:
				return `The ${category} of this campaign (<em>${value}</em>) is no longer supported.`;
			case InvalidCampaignPropertyCategories.BUDGET_MISMATCH:
				return `Campaigns for which budget_total is not the sum of budget and custom_products_budget are no longer supported.`;
			case InvalidCampaignPropertyCategories.NO_GEO_BREAKDOWN:
			case InvalidCampaignPropertyCategories.INCLUDE_NON_PREMIUM_PROGRAMMES:
				return `Campaigns with the ${category} setting for old reporting enabled are no longer supported.`;
			case InvalidCampaignPropertyCategories.GEOTARGETING:
				return 'Campaigns with geotargeting disabled are no longer supported.';
			case InvalidCampaignPropertyCategories.TABLEAU_REPORTING:
				return 'Campaigns that are not onboarded to Tableau reporting are no longer supported.';
			case InvalidCampaignPropertyCategories.INTERNAL:
				return 'Internal campaigns are no longer supported.';
			case InvalidCampaignPropertyCategories.FIXED_END_DATE:
				return 'Campaigns with the fixed-end-date setting enabled are no longer supported.';
			case InvalidCampaignPropertyCategories.CURRENCY:
			case InvalidCampaignPropertyCategories.CLIENT_SERVICES_PERSON:
			case InvalidCampaignPropertyCategories.ACCOUNT_MANAGER:
			case InvalidCampaignPropertyCategories.CRM_ID:
			case InvalidCampaignPropertyCategories.START_DATE:
			case InvalidCampaignPropertyCategories.END_DATE:
				return 'Campaign configuration seems to be incomplete.'
					+ ` The ${category} setting of this campaign (<em>${value}</em>) is invalid.`;
			default:
				return 'One of the settings of this campaign is no longer supported.';
		}
	});

	private invalidCampaignPropertyDetails = computed((): InvalidCampaignPropertyDetails => {
		return store.getters.invalidCampaignPropertyDetails();
	});

	public handleCampaignDetailsRetrievalError(error: HttpErrorResponse): void {
		const unsupportedPropertyMessages = Object.values(InvalidCampaignPropertyKeywords);
		const errorResponse = JSON.parse(error.response?.text ?? '{ "message": "" }') as Error;
		const errorRelatedToUnsupportedProperty = unsupportedPropertyMessages.some((message) => {
			return errorResponse.message.includes(this.nameOfBackEndClass) && errorResponse.message.includes(message);
		});
		if (!errorRelatedToUnsupportedProperty) {
			rollbarOperator.triggerError(error as Error);
			store.mutations.setModalErrorState(ModalErrorStates.GET_GENERAL_CAMPAIGN_DATA);
			return;
		}

		store.mutations.setModalErrorState(ModalErrorStates.UNSUPPORTED_CAMPAIGN_SETTING);
		this.setInvalidCampaignPropertyDetailsBasedOnError(errorResponse.message);
	}

	public isErrorRelatedToUnsupportedProperty(error: HttpErrorResponse): boolean {
		const errorResponse = JSON.parse(error.response?.text ?? '{ "errorCode": 0 }') as HttpErrorResponseWithErrorCode;
		// TODO (CITA-20866): Use the enum value for the error code instead of a hardcoded number.
		return errorResponse.errorCode === 2402;
	}

	private setInvalidCampaignPropertyDetailsBasedOnError(errorMessage: string): void {
		const keywordRelevantForError = Object.values(InvalidCampaignPropertyKeywords).find((keyword) => errorMessage.includes(keyword));
		switch (keywordRelevantForError) {
			case InvalidCampaignPropertyKeywords.PARTNERSHIP_TYPE:
				this.setInvalidCampaignPropertyDetailsForPartnershipType(errorMessage);
				break;
			case InvalidCampaignPropertyKeywords.REGION:
				this.setInvalidCampaignPropertyDetailsForRegion(errorMessage);
				break;
			case InvalidCampaignPropertyKeywords.BUDGET:
				this.setInvalidCampaignPropertyDetailsForBudget(errorMessage);
				break;
			case InvalidCampaignPropertyKeywords.BUDGET_TOTAL:
				this.setInvalidCampaignPropertyDetailsForBudgetTotal(errorMessage);
				break;
			case InvalidCampaignPropertyKeywords.ADWORDS:
				this.setInvalidCampaignPropertyDetailsForAdwordsDate(errorMessage);
				break;
			case InvalidCampaignPropertyKeywords.GEOTARGETING:
				this.setInvalidCampaignPropertyDetailsForGeotargeting();
				break;
			case InvalidCampaignPropertyKeywords.TABLEAU_REPORTING:
				this.setInvalidCampaignPropertyDetailsForTableauReporting();
				break;
			case InvalidCampaignPropertyKeywords.SUPPORTED_OPTIONS:
				this.setInvalidCampaignPropertyDetailsForSupportedOptions(errorMessage);
				break;
			case InvalidCampaignPropertyKeywords.CURRENCY:
				this.setInvalidCampaignPropertyDetailsForCurrency(errorMessage);
				break;
			case InvalidCampaignPropertyKeywords.CLIENT_SERVICES_PERSON:
				this.setInvalidCampaignPropertyDetailsForClientServicesPerson(errorMessage);
				break;
			case InvalidCampaignPropertyKeywords.ACCOUNT_MANAGER:
				this.setInvalidCampaignPropertyDetailsForAccountManager(errorMessage);
				break;
			case InvalidCampaignPropertyKeywords.CRM_ID:
				this.setInvalidCampaignPropertyDetailsForCrmId(errorMessage);
				break;
			case InvalidCampaignPropertyKeywords.START_DATE:
				this.setInvalidCampaignPropertyDetailsForDate(errorMessage, InvalidCampaignPropertyCategories.START_DATE);
				break;
			case InvalidCampaignPropertyKeywords.END_DATE:
				this.setInvalidCampaignPropertyDetailsForDate(errorMessage, InvalidCampaignPropertyCategories.END_DATE);
				break;
			default:
				throw new Error(
					'CampaignErrorPresenter.setInvalidCampaignPropertyDetailsBasedOnError: invalid campaign property keyword not covered.'
				);
		}

		redshiftTracker.trackUnsupportedCampaignLoad();
	}

	private setInvalidCampaignPropertyDetailsForPartnershipType(errorMessage: string): void {
		const messageParts = errorMessage.split(this.defaultValueSeparator);
		if (messageParts.length < 2) {
			return;
		}

		store.mutations.setInvalidCampaignPropertyDetails({
			category: InvalidCampaignPropertyCategories.PARTNERSHIP_TYPE,
			value: messageParts[1].trim()
		});
	}

	private setInvalidCampaignPropertyDetailsForRegion(errorMessage: string): void {
		const messageParts = errorMessage.split(this.defaultValueSeparator);
		if (messageParts.length < 2) {
			return;
		}

		store.mutations.setInvalidCampaignPropertyDetails({
			category: InvalidCampaignPropertyCategories.REGION,
			value: messageParts[1].trim()
		});
	}

	private setInvalidCampaignPropertyDetailsForBudget(errorMessage: string): void {
		const messageParts = errorMessage.split(this.defaultValueSeparator);
		if (messageParts.length < 2) {
			return;
		}

		store.mutations.setInvalidCampaignPropertyDetails({
			category: InvalidCampaignPropertyCategories.BUDGET,
			value: messageParts[1].trim()
		});
	}

	private setInvalidCampaignPropertyDetailsForBudgetTotal(errorMessage: string): void {
		const messageParts = errorMessage.split(this.defaultValueSeparator);
		if (messageParts.length !== 1) {
			return;
		}

		store.mutations.setInvalidCampaignPropertyDetails({
			category: InvalidCampaignPropertyCategories.BUDGET_MISMATCH,
			value: ''
		});
	}

	private setInvalidCampaignPropertyDetailsForAdwordsDate(errorMessage: string): void {
		const initialMessageParts = errorMessage.split(this.defaultValueSeparator);
		initialMessageParts[0] = initialMessageParts[0].replace(`${this.nameOfBackEndClass}`, '');
		if (initialMessageParts.length < 2) {
			return;
		}

		let category = InvalidCampaignPropertyCategories.ADWORDS_START;
		const smallerMessageParts = initialMessageParts[0].split(' ').filter((part) => part);
		if (smallerMessageParts[0].includes('_end')) {
			category = InvalidCampaignPropertyCategories.ADWORDS_END;
		}

		store.mutations.setInvalidCampaignPropertyDetails({
			category,
			value: initialMessageParts[1].trim()
		});
	}

	private setInvalidCampaignPropertyDetailsForGeotargeting(): void {
		store.mutations.setInvalidCampaignPropertyDetails({
			category: InvalidCampaignPropertyCategories.GEOTARGETING,
			value: ''
		});
	}

	private setInvalidCampaignPropertyDetailsForTableauReporting(): void {
		store.mutations.setInvalidCampaignPropertyDetails({
			category: InvalidCampaignPropertyCategories.TABLEAU_REPORTING,
			value: ''
		});
	}

	private setInvalidCampaignPropertyDetailsForSupportedOptions(errorMessage: string): void {
		const initialMessageParts = errorMessage.split('Unsupported value:');
		if (initialMessageParts.length < 2) {
			return;
		}

		const smallerMessageParts = initialMessageParts[1].split(';');

		let category = InvalidCampaignPropertyCategories.INTERNAL;
		switch (smallerMessageParts[0].trim()) {
			case 'no_geo_breakdown':
				category = InvalidCampaignPropertyCategories.NO_GEO_BREAKDOWN;
				break;
			case 'show_non_premiums':
				category = InvalidCampaignPropertyCategories.INCLUDE_NON_PREMIUM_PROGRAMMES;
				break;
			case 'fixed_end_date':
				category = InvalidCampaignPropertyCategories.FIXED_END_DATE;
				break;
		}

		store.mutations.setInvalidCampaignPropertyDetails({
			category,
			value: ''
		});
	}

	private setInvalidCampaignPropertyDetailsForCurrency(errorMessage: string): void {
		const messageParts = errorMessage.split(this.defaultValueSeparator);
		if (messageParts.length < 2) {
			return;
		}

		store.mutations.setInvalidCampaignPropertyDetails({
			category: InvalidCampaignPropertyCategories.CURRENCY,
			value: messageParts[1].trim()
		});
	}

	private setInvalidCampaignPropertyDetailsForClientServicesPerson(errorMessage: string): void {
		const messageParts = errorMessage.split(this.defaultValueSeparator);
		if (messageParts.length < 2) {
			return;
		}

		store.mutations.setInvalidCampaignPropertyDetails({
			category: InvalidCampaignPropertyCategories.CLIENT_SERVICES_PERSON,
			value: messageParts[1].trim()
		});
	}

	private setInvalidCampaignPropertyDetailsForAccountManager(errorMessage: string): void {
		const messageParts = errorMessage.split(this.defaultValueSeparator);
		if (messageParts.length < 2) {
			return;
		}

		store.mutations.setInvalidCampaignPropertyDetails({
			category: InvalidCampaignPropertyCategories.ACCOUNT_MANAGER,
			value: messageParts[1].trim()
		});
	}

	private setInvalidCampaignPropertyDetailsForCrmId(errorMessage: string): void {
		const messageParts = errorMessage.split(this.defaultValueSeparator);
		if (messageParts.length < 2) {
			return;
		}

		store.mutations.setInvalidCampaignPropertyDetails({
			category: InvalidCampaignPropertyCategories.CRM_ID,
			value: messageParts[1].trim()
		});
	}

	private setInvalidCampaignPropertyDetailsForDate(
		errorMessage: string,
		dateType: InvalidCampaignPropertyCategories.START_DATE | InvalidCampaignPropertyCategories.END_DATE
	): void {
		const messageParts = errorMessage.split(this.defaultValueSeparator);
		if (messageParts.length < 2) {
			return;
		}

		store.mutations.setInvalidCampaignPropertyDetails({
			category: dateType,
			value: messageParts[1].trim()
		});
	}
}

export default new CampaignErrorPresenter();
